/**
 * Where the day form is put — the builder's 574px side panel, or the day modal over the week.
 *
 * ── One form, ONE host component, and the box is a prop ──────────────────────────────────────
 * `features/plan-day-programming` renders identically in both boxes, down to the head and the
 * library drawer. What this file decides is which box `DayPanelDialog` draws. It used to choose
 * between two component TYPES at one tree position, which unmounted the form on every switch and
 * threw the trainer's superset selection, fold state and scroll offset away — see
 * `features/plan-day-programming`'s `DayPanelHost.tsx`, where the box lives since 2 October 2026.
 *
 * ── The modal REPLACED a different fullscreen host on 13 September 2026 ──────────────────────
 * `?dock=full` used to mount a 26px-inset card with its own breadcrumb head, its own Zapisz, and the
 * library as a 460px column beside the list — `Dzien - pelny ekran`'s design, unreachable from the
 * UI since 7 September. The owner's mock (`Builder planu - modal - progresja`) draws something else
 * entirely: this app's own panel, in a box 80% of the screen wide. The old host is deleted rather
 * than kept beside it, and `?dock=full` keeps its wire spelling so an old link and a restored
 * session open the new modal rather than crash.
 *
 * ── Which box a day OPENS in is a remembered default ────────────────────────────────────────
 * `model/dockDefault`, written by the maximise / restore pair in the head and read by
 * `useDayDock.open`. The URL still owns WHICH day is open and in which box it is right now; storage
 * holds only the preference. See both files' headers.
 *
 * ── `modal` differs between the two, and it is the difference that matters ───────────────────
 * The PANEL is NOT modal. The MODAL is, because it covers the week and there is nothing behind it
 * to read.
 *
 * The panel was `'trap-focus'`, which keeps the week visible but marks the rest of the document
 * `aria-hidden`, and a subtree hidden from assistive technology must not stay clickable — so the
 * week was made `inert` to match. The owner asked for the opposite (7 September 2026): a trainer
 * steps between days by pressing the card they want and puts the panel away by pressing the week
 * beside it, which is how the handoff behaves.
 *
 * `false` is the only value that makes both true without lying to a screen reader: nothing behind
 * the panel is hidden, so nothing behind it has to refuse a pointer, and the top bar's save is
 * reachable again. What it costs is the trap — Tab walks out of the panel and into the week, which
 * is what a non-modal side panel is.
 *
 * ── Focus return is the PAGE's, so `finalFocus` is off ───────────────────────────────────────
 * Base UI restores focus to the opener, and under the modal the opener may be behind a scrim at that
 * moment. `useDayDock` remembers the day instead and the card's own button takes focus when the dock
 * closes. One mechanism for both boxes; see that hook's header.
 *
 * ── Initial focus is the HEADING, and it is named rather than left to the DOM order ───────────
 * Base UI's default is the first tabbable element inside the popup, and in both boxes that is a day
 * arrow — the ‹ that LEAVES the day. The worst case is the one the tape creates: press "+ Trening",
 * land in the dock on the day you just made, press Enter, and you are looking at a different day
 * with no idea the new one exists. The heading is the honest target: it names what is open, and the
 * next Tab reaches the same controls in the same order.
 *
 * It is resolved by id rather than by a ref because the element belongs to `plan-day-programming` —
 * this file owns `headingId` and hands it down, so the lookup uses the one address both sides
 * already agree on rather than threading a ref through a feature boundary for it. `initialFocus`
 * accepts a function returning an element (verified in
 * `node_modules/@base-ui/react/dialog/popup/DialogPopup.d.ts`), and returning `null` falls back to
 * the default — which is the right answer if the heading ever fails to render.
 *
 * ── Escape and Back mean the same thing, and both go through the URL ─────────────────────────
 * `onOpenChange(false)` fires for Escape; it calls `close`, which removes `day` and `dock` from the
 * address. Back removes them too, by being the browser. Nothing here holds open state that could
 * disagree with what the URL says.
 *
 * The one interception is the library drawer, which absorbs the Escape that closes IT — and it is
 * made in `DayPanelDialog`'s `onOpenChange`, rather than in a `onKeyDown` on the popup. That handler existed in
 * both boxes and never worked, and the reason is Base UI's own wiring, read in
 * `node_modules/@base-ui/react/floating-ui-react/hooks/useDismiss.js`: `closeOnEscapeKeyDown` is
 * handed to the POPUP as a React `onKeyDown` of its own, which runs before the external handler
 * merged onto the same element, and the same callback is registered on the `document` in the BUBBLE
 * phase. Either way the dialog had already been asked to close by the time our handler saw the key,
 * so `stopPropagation` had nothing left to stop and Escape inside the drawer took the whole dock
 * down. (An earlier note here blamed React's synthetic phase running after a CAPTURE-phase document
 * listener; the source says bubble, and that reading is withdrawn.) It LOOKED right only because
 * the rail's search field stops the key natively, so the one place a trainer's focus usually sits
 * was the one place Escape did nothing at all. Declining to close the dialog is the honest interception: Base UI asks whether it
 * may close, and while the catalogue is over the day the answer is "close that instead".
 *
 * ── A click outside does NOT dismiss, and that is what makes the live week usable ────────────
 * Found by driving it: with Base UI's default pointer dismissal, the FIRST click anywhere outside
 * the panel is consumed by closing it. That made Save unreachable in one press while the dock was
 * open, and it made clicking another day's card a race between the close and the card's own
 * handler. Both are the panel's whole point — the week beside it is meant to stay usable — so
 * dismissal is disabled and the two deliberate exits (Escape, the ✕) are the only ones. Back is a
 * third, and it belongs to the browser rather than to this component.
 *
 * It is off in the MODAL too, and that is the same answer for a different reason: the modal's scrim
 * is the whole screen, so a press that dismissed on it would put away a day the trainer is
 * programming on the first stray click. The ✕, the restore and Escape are the ways out.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { ExerciseRail } from '~/features/exercise-library-rail';
import { dayProgression } from '~/features/plan-progression';
import { DayForm, DayPanelDialog, type DayDockState } from '~/features/plan-day-programming';

import type { DayMoveTargetView } from '../model/builderChrome';

interface DayDockProps {
  readonly dock: DayDockState;
  /**
   * The phase's programmed days, from the week's view model. The dock stamps `isCurrent` on, because
   * which day is open is the URL's answer rather than the week's.
   */
  readonly dayTargets: readonly DayMoveTargetView[];
  /**
   * What the plan's save has to say right now — "Zapisano", a refusal, or an empty string. Said
   * inside the MODAL, where the bar's own region cannot be heard.
   *
   * `modal={isFull}`, so Base UI's `ariaHidden` applies to the modal alone: measured 13 September
   * 2026, with a day open, the screen's `<header>` carries `aria-hidden="true"` under the modal and
   * nothing carries it under the panel. That is precisely when Cmd/Ctrl+S is the only save a
   * keyboard trainer can reach, so the modal repeats the words; the panel does not, and `DayPanelHost`
   * is where that is decided. An earlier note here claimed both boxes hid the document, which is how
   * the panel came to announce every save twice.
   */
  readonly saveAnnouncement: string;
  /** The Buddy panel is open: the day panel stands to its left, under the top bar. */
  readonly besideAssistant: boolean;
}

export function DayDock({ dock, dayTargets, saveAnnouncement, besideAssistant }: DayDockProps) {
  const headingId = useId();
  const { t, i18n } = useTranslation();
  const isFull = dock.dock === 'full';
  // The composition the layer rule asks for: `plan-day-programming` draws the exercise,
  // `plan-progression` draws its rule and its projection, and neither imports the other — this page
  // is the layer entitled to know both.
  const progression = dayProgression({ t, locale: i18n.language, announce: dock.announce });

  const form =
    dock.daySlotToken === null || dock.dock === null ? null : (
      <DayForm
        daySlotToken={dock.daySlotToken}
        headingId={headingId}
        onSelectDay={dock.selectDay}
        onClose={dock.close}
        onRequestLibrary={dock.requestLibrary}
        announce={dock.announce}
        /* Neither box draws a Save. The programator mock saves from the ember button in the screen's
         * top bar and the modal's mock draws no Zapisz either, so Cmd/Ctrl+S is the keyboard route
         * and `DayPanelHead` prints it on the heading's description precisely when `save` is null. */
        save={null}
        dayTargets={dayTargets.map((target) => ({
          ...target,
          isCurrent: target.slotToken === dock.daySlotToken,
        }))}
        progression={progression}
        /* The drawer that put this card there is now shut, and the row that was pressed went with
         * it. See `useDayDock`. */
        focusExerciseSlotToken={dock.focusExerciseToken}
        /* The owner's pair, over the mock, which draws neither: maximise in the panel and restore in
         * the modal, in the head's right-hand cluster just before the ✕. `switchHost` writes the
         * remembered default and rewrites `?dock=` in place. */
        hostSwitch={{
          kind: isFull ? 'restore' : 'maximise',
          onPress: () => {
            const next = isFull ? 'panel' : 'full';
            dock.switchHost(next);
            /* Said out loud, because nothing else about the switch is: the control keeps focus and
             * keeps its slot, the day is the same day, and the fact that every FOLLOWING day now
             * opens here too is a preference the press just wrote with no visible sign of it. The
             * dock's own region carries it — `t` lives on this layer, not in `useDayDock`. */
            dock.announce(t(`web:planBuilder.day.${next === 'full' ? 'maximised' : 'restored'}`));
          },
        }}
      />
    );

  return (
    /* See the header: the panel is non-modal so the week beside it stays readable and clickable;
     * the modal is modal because it covers the week. The host IS the popup, and it stays mounted
     * across a host switch, which is the point. */
    <DayPanelDialog
      modal={isFull}
      host={isFull ? 'full' : 'panel'}
      dock={dock}
      headingId={headingId}
      announcement={dock.announcement}
      saveAnnouncement={saveAnnouncement}
      form={form}
      besideAssistant={besideAssistant}
      renderLibrary={(props) => <ExerciseRail {...props} />}
    />
  );
}
